// ===================================
// PROFILE PAGE - src/pages/Profile.tsx
// ===================================
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { User, Mail, Building, Shield, Settings, Save } from "lucide-react";
import { notifySuccess } from "../utils/Toastify";

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });

  const handleSave = () => {
    updateProfile(formData);
    setIsEditing(false);
    notifySuccess("Your profile has been successfully updated.");
  };

  const getRoleColor = (roleLevel: number) => {
    if (roleLevel <= 1) return "bg-red-100 text-red-800";
    if (roleLevel <= 3) return "bg-blue-100 text-blue-800";
    if (roleLevel <= 6) return "bg-green-100 text-green-800";
    return "bg-[#020617] text-[#64748b]";
  };

  const permissions: string[] = Array.isArray(user?.permission)
    ? user!.permission
    : user?.permission?.split(",") || [];

  return (
    <div className="min-h-screen bg-[#0f172a]">
      <Header
        title="Profile Settings"
        subtitle="Manage your account information"
      />

      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Profile Card */}
          <div className="card mb-6 !bg-[#1e293b] !border-[#334155] border">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-white">
                  Account Information
                </h3>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="btn-secondary text-sm flex items-center space-x-1"
                >
                  <Settings className="w-4 h-4" />
                  <span>{isEditing ? "Cancel" : "Edit"}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Picture */}
                <div className="md:col-span-2 flex items-center space-x-4">
                  <div className="w-16 h-16 bg-[#8b3dff] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white">
                      {user?.name}
                    </h4>
                    <p className="text-sm text-[#64748b]">{user?.email}</p>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="input-field text-white !border-[#334155] border focus:!outline-[#8b3dff] focus:ring-2 "
                      style={{ backgroundColor: "#020617" }}
                    />
                  ) : (
                    <p className="text-white">{user?.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="input-field text-white !border-[#334155] border focus:!outline-[#8b3dff] focus:ring-2 "
                      style={{ backgroundColor: "#020617" }}
                    />
                  ) : (
                    <p className="text-white">{user?.email}</p>
                  )}
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    Username
                  </label>
                  <p className="text-white">{user?.user_name}</p>
                  <p className="text-xs text-[#64748b]">
                    Username cannot be changed
                  </p>
                </div>

                {/* Business */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <Building className="w-4 h-4 inline mr-1" />
                    Business
                  </label>
                  <p className="text-white">{user?.current_business}</p>
                  <p className="text-xs text-[#64748b]">
                    Business ID: {user?.business_id}
                  </p>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Role & Permissions
                  </label>
                  <div className="space-y-2">
                    <span
                      className={`px-2 py-1 text-xs rounded ${getRoleColor(
                        user?.role_level || 8
                      )}`}
                    >
                      {user?.role_name} (Level {user?.role_level})
                    </span>
                    <p className="text-xs text-[#64748b]">
                      Department: {user?.department_name || "Not assigned"}
                    </p>
                    <p className="text-xs text-[#64748b]">
                      Profile: {user?.profile_name}
                    </p>
                    {user?.is_admin && (
                      <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">
                        Administrator
                      </span>
                    )}
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    Permissions
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {permissions.map((perm, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-[#8b3dff] rounded"
                      >
                        {perm.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="bg-[#8b3dff] px-4 py-1 rounded-md text-white flex items-center space-x-1"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Activity Summary */}
          <div className="card !bg-[#1e293b] !border-[#334155] border">
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4">
                Account Activity
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#64748b]">
                    Account created
                  </span>
                  <span className="text-sm text-white">Phase 1 POC User</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#64748b]">Last login</span>
                  <span className="text-sm text-white">Current session</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#64748b]">Access level</span>
                  <span className="text-sm text-white">
                    {user?.role_level === 0
                      ? "Full Access"
                      : user?.role_level && user.role_level <= 3
                      ? "Management Access"
                      : user?.role_level && user.role_level <= 6
                      ? "Standard Access"
                      : "Limited Access"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
